<template>
  <div
    class="min-h-screen bg-gradient-to-br from-[#FFF9EC] via-white to-[#FFECBD] flex items-center justify-center px-4 relative overflow-hidden"
  >
    <!-- Decorative elements -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <!-- Animated mosque silhouette patterns -->
      <div
        class="absolute top-10 left-10 w-32 h-32 text-[#D0A444] opacity-5 animate-float"
      >
        <svg viewBox="0 0 100 100" fill="currentColor">
          <path
            d="M50,10 L55,30 L75,30 L60,45 L65,65 L50,50 L35,65 L40,45 L25,30 L45,30 Z"
          />
        </svg>
      </div>
      <div
        class="absolute bottom-20 right-20 w-40 h-40 text-[#D0A444] opacity-5 animate-float-delayed"
      >
        <svg viewBox="0 0 100 100" fill="currentColor">
          <circle cx="50" cy="50" r="40" />
        </svg>
      </div>
      <div
        class="absolute top-1/2 left-1/4 w-24 h-24 text-[#FFECBD] opacity-30 animate-pulse"
      >
        <svg viewBox="0 0 100 100" fill="currentColor">
          <path d="M50,20 Q30,40 50,60 Q70,40 50,20 Z" />
        </svg>
      </div>
    </div>

    <!-- Main content -->
    <div class="relative z-10 max-w-3xl w-full">
      <div class="text-center space-y-8">
        <!-- 404 Number with Arabic calligraphy inspiration -->
        <div class="relative">
          <h1
            class="font-outfit font-semibold text-[140px] sm:text-[180px] lg:text-[220px] leading-none text-[#D0A444] opacity-20 select-none"
          >
            404
          </h1>
          <div class="absolute inset-0 flex items-center justify-center">
            <div
              class="w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 rounded-full bg-[#D0A444] bg-opacity-10 backdrop-blur-sm flex items-center justify-center animate-pulse-slow"
            >
              <!-- Kaaba icon -->
              <svg
                class="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-[#D0A444]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
              >
                <rect x="6" y="8" width="12" height="12" rx="1" />
                <path d="M6 8L12 4L18 8" />
                <path d="M12 4V8" />
                <line x1="6" y1="12" x2="18" y2="12" />
                <line x1="6" y1="16" x2="18" y2="16" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Message -->
        <div class="space-y-4 px-4">
          <h2
            class="font-outfit font-semibold text-2xl sm:text-3xl lg:text-4xl text-gray-800"
          >
            Ce chemin n'existe pas
          </h2>
          <p
            class="font-outfit text-base sm:text-lg text-gray-600 max-w-xl mx-auto leading-relaxed"
          >
            Comme un pèlerin égaré dans le désert, cette page semble
            introuvable. Mais ne vous inquiétez pas, nous allons vous ramener
            sur le bon chemin.
          </p>
        </div>

        <!-- Action buttons -->
        <div
          class="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
        >
          <a
            href="/"
            class="group inline-flex items-center justify-center gap-2 bg-[#D0A444] hover:bg-[#E0AF39] text-white font-outfit font-medium text-base px-8 py-3.5 rounded-full shadow-button transition-all duration-300 hover:shadow-lg hover:scale-105 w-full sm:w-auto"
          >
            <svg
              class="w-5 h-5 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            <span>Retour à l'accueil</span>
          </a>

          <a
            href="/contact"
            class="group inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-outfit font-medium text-base px-8 py-3.5 rounded-full border border-gray-300 shadow-button transition-all duration-300 hover:shadow-lg hover:border-[#D0A444] hover:border-opacity-50 w-full sm:w-auto"
          >
            <svg
              class="w-5 h-5 group-hover:rotate-12 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <span>Contactez-nous</span>
          </a>
        </div>

        <!-- Popular links -->
        <div class="pt-8 border-t border-gray-200 border-opacity-50 mt-12">
          <p class="font-outfit text-sm text-gray-500 mb-4">Liens populaires</p>
          <div class="flex flex-wrap items-center justify-center gap-3">
            <a
              href="/"
              class="inline-flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-[#D0A444] hover:bg-opacity-5 text-gray-700 hover:text-[#D0A444] font-outfit text-sm font-medium rounded-full border border-gray-200 hover:border-[#D0A444] hover:border-opacity-30 transition-all duration-200"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
              Formules Omra
            </a>
            <a
              href="/contact"
              class="inline-flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-[#D0A444] hover:bg-opacity-5 text-gray-700 hover:text-[#D0A444] font-outfit text-sm font-medium rounded-full border border-gray-200 hover:border-[#D0A444] hover:border-opacity-30 transition-all duration-200"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Contact
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  layout: false,
  requiresAuth: false,
});

useHead({
  title: "404 - Page introuvable",
  meta: [
    {
      name: "description",
      content: "Cette page n'existe pas ou a été déplacée.",
    },
  ],
});
</script>

<style scoped>
@keyframes float {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(5deg);
  }
}

@keyframes float-delayed {
  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-15px) rotate(-5deg);
  }
}

@keyframes pulse-slow {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float-delayed 8s ease-in-out infinite;
  animation-delay: 1s;
}

.animate-pulse-slow {
  animation: pulse-slow 3s ease-in-out infinite;
}
</style>
